// --------------------------
// IMPORTS
// --------------------------
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import useCurrentPath, {
  decryptData,
  downloadMonthWiseExpenseRequestReport,
} from "../../utils/Constants";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import { useSearchAndPagination } from "../../utils/useSearchAndPagination";
import {
  getExpenseRequestsListThunk,
  searchExpenseDataThunk,
} from "../../store/thunks/listOfExpensesThunk";
import CustomLoader from "../../utils/CustomLoader";
import { useLocation } from "react-router-dom";
import APICaller from "../../utils/APICaller";
import html2pdf from "html2pdf.js";
import ExportPdf from "../../utils/exportPdf";
import ReactDOMServer from "react-dom/server";
import TableContent from "../../components/atoms/TableContent";
import moment from "moment";
import {
  getCurrencyListDetailThunk,
  getDepartmentListThunk,
  getExpenseCodeListThunk,
  getProjectCodeListThunk,
  getUserListWithDetailThunk,
} from "../../store/thunks/masterDataThunk";
import { Button, message, Tooltip } from "antd";
import media from "../../media";
import ExpenseListingDetail from "./expenseListingDetail";
import DraftExpenseRequest from "./draftExpenseRequest";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function ListOfExpense() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const location = useLocation();
  const autoSearchDone = useRef(false);

  // --------------------------
  // REDUX STATE
  // --------------------------
  const {
    storeRole,
    storeLIUId,
    storeToken,
    storeUserInfo,
    storeAccessTokenExpiresOn,
  } = useSelector(
    (state) => ({
      role: state.auth.role,
      lIUId: state.auth.lIUId,
      accessToken: state.auth.accessToken,
      userInfo: decryptData(state.auth.userInfo),
      accessTokenExpiresOn: state.auth.accessTokenExpiresOn,
    }),
    shallowEqual
  );
  const storeEmpID = storeUserInfo?.user?.empID;
  const storeList = useSelector(
    (state) => state.auth.list?.[currentPath + "_info"] || [],
    shallowEqual
  );
  const listOfExpenses =
    useSelector((state) => state.listOfExpenses.list) || [];

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [searchFilterData, setSearchFilterData] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [filteredListOfExpenses, setFilteredListOfExpenses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("ALL");
  const [isFilteredDates, setIsFilteredDates] = useState(false);
  const [filterValues, setFilterValues] = useState(null);
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [startRange, setStartRange] = useState("");
  const [endRange, setEndRange] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [selectedRecordKey, setSelectedRecordKey] = useState(null);
  const [actionViewSelected, setActionViewSelected] = useState(null);
  const [hasSelectedRows, setHasSelectedRows] = useState([]);

  // --------------------------
  // LOCAL VARIABLES
  // --------------------------

  const directDetailPageRenderedID = location.pathname?.split("/")?.[2];

  // Memoized filtered list (remove unnecessary keys)
  const filteredList = useMemo(() => {
    if (Array.isArray(storeList)) {
      return storeList.map(({ key, id, ...rest }) => rest);
    }
    return [];
  }, [storeList]);

  // --------------------------
  // HANDLERS
  // --------------------------

  const handleView = (key, viewTitle) => {
    setSelectedRecordKey(key);
    setIsVisible(true);
    setActionViewSelected(viewTitle);
  };

  const generatePdfContent = (
    record,
    expenseDetail,
    storeRole,
    storeToken,
    storeAccessTokenExpiresOn
  ) => {
    return ReactDOMServer.renderToString(
      <ExportPdf
        record={record}
        expenseDetail={expenseDetail}
        storeRole={storeRole}
        storeToken={storeToken}
        storeAccessTokenExpiresOn={storeAccessTokenExpiresOn}
      />
    );
  };

  const handlePdfExport = async (record) => {
    try {
      // Fetch expense details
      const endpoint = `/list-of-expense/${record?.key}`;
      const method = "GET";
      const payload = {};
      const response = await APICaller(
        storeRole,
        storeToken,
        endpoint,
        method,
        payload,
        storeAccessTokenExpiresOn
      );

      //setExpenseDetail(response);

      if (response) {
        // Step 1: Generate HTML content as a string
        const htmlContent = generatePdfContent(
          record,
          response,
          storeRole,
          storeToken,
          storeAccessTokenExpiresOn
        );

        // Define PDF options
        const options = {
          margin: [10, 10, 10, 10],
          filename: `${record.key}_Details.pdf`,
          html2canvas: {
            scale: 2, // Increase scale for better resolution
            useCORS: true,
            logging: true,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
          },
        };

        // Generate PDF
        html2pdf()
          .from(htmlContent)
          .set(options)
          .save()
          .catch((error) => {
            console.error("Error generating PDF:", error);
          });
      }
    } catch (error) {
      console.error("Error fetching data or generating PDF:", error);
    }
  };

  // Month filter handler
  const handleMonthRangeChange = (range) => {
    setSearchFilterData("");
    if (range && range.length === 2) {
      const startMonth = range[0]?.format("YYYY-MM");
      const endMonth = range[1]?.format("YYYY-MM");

      setFilterValues({ startMonth, endMonth });
      setIsFilteredDates(true);
    } else {
      setFilterValues(null);
      setIsFilteredDates(false);
    }
  };

  // Download MWER report
  const handleDownloadMWERReport = async () => {
    const hasSearchValue = document.querySelector(
      ".dashboardSearch input"
    ).value;

    setShowLoader(true);
    downloadMonthWiseExpenseRequestReport(
      storeRole,
      storeToken,
      storeLIUId,
      storeAccessTokenExpiresOn,
      currentPath,
      currentStatus,
      hasSearchValue,
      setShowLoader
    );
  };

  // Pagination and search
  const { handleGlSearch, handlePaginationChange, pagination } =
    useSearchAndPagination({
      searchThunk: searchExpenseDataThunk,
      listThunk: getExpenseRequestsListThunk,
      currentPath,
      role: storeRole,
      lIUId: storeLIUId,
      setFilteredData: setFilteredData,
      setStoreTotalCount,
      status: currentStatus,
      month: startRange && endRange ? { startRange, endRange } : "",
      onSearchStatusChange: (searching, count) => {
        setIsSearching(searching);
        //setShowLoader(searching);
        setSearchCount(count);
      },
    });

  // Callback to get filtered data from ExpenseMultitasking
  const handleFilteredData = (data) => {
    //setFilteredData(data);
    setStoreTotalCount(data?.length || 0);
    setShowLoader(false);
  };

  const onPageChange = async (page, pageSize, currentSearchText = "") => {
    const trimmedSearch = currentSearchText && currentSearchText; //?.trim()
    setShowLoader(true);

    try {
      if (trimmedSearch) {
        await handleGlSearch({
          value: trimmedSearch,
          page,
          pageSize,
        });
      } else {
        await handlePaginationChange(page, pageSize);
      }
    } finally {
      setShowLoader(false); // <-- Always runs when request is done
    }
  };

  // Tab change handler
  const handleTabClick = useCallback((status) => {
    setShowLoader(true);
    setFilteredData([]); // clear old table data
    setStoreTotalCount(0);
    setCurrentStatus(status);
  }, []);

  const handleDraftExpenseForPublish = (id) => {
    setShowLoader(true);
    const endpoint = `/publishMulExpense`;
    const method = "PUT";
    const payload = {
      action: "publishMul",
      ids: [id],
      historySource: "listing-page-publish-button",
      historyUserID: storeLIUId,
      historyAction: "Publishing drafted expense",
      status: process.env.REACT_APP_PENDING_STATUS.toUpperCase(),
    };

    const messageKey = "publishDraftExpenseError";
    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn
    )
      .then((response) => {
        setShowLoader(false);

        if (response.statusCode) {
          if (response.message) {
            message.open({
              key: messageKey,
              type: response.statusCode === 400 ? "error" : "success",
              duration: 0, // stay until closed
              content: (
                <div className="customMsgPopup">
                  <span>{response.message}</span>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => message.destroy(messageKey)}
                  >
                    X
                  </Button>
                </div>
              ),
            });
          } else {
            setHasSelectedRows("");
            message.destroy("publishDraftExpenseError");
            message.success("Expense published successfully.");
          }
          //onRefresh();
        }
      })
      .catch((error) => {
        setShowLoader(false);
        message.error("Something went wrong!");
      });
  };

  // --------------------------
  // API CALLS
  // --------------------------
  const getExpenseRequestsList = async (source = "", pagination = "") => {
    setSearchFilterData?.("");
    setStoreTotalCount("");
    const inputElement = document.querySelector(".dashboardSearch span input");
    const hasSearchValue = inputElement?.value || "";

    const allStates = {
      showLoader,
      setShowLoader,
      pagination: pagination || { current: 1, page: 1, pageSize: 50 },
      status: currentStatus,
      filterValues: { startRange, endRange },
      searchKeyword: hasSearchValue || "",
    };

    try {
      await dispatch(
        getExpenseRequestsListThunk({
          route: location.pathname.replace("/", ""),
          allStates,
        })
      )
        .unwrap()
        .then((response) => {
          if (response?.data) {
            setFilteredData(response?.data);
            setStoreTotalCount(response?.totalCount);
          }
        });
    } catch (error) {
      console.error("Error fetching expense requests list: ", error);
    } finally {
      setShowLoader(false);
    }
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------

  // Update filteredData only when necessary
  useEffect(() => {
    if (!isFilteredDates && filteredData !== filteredList) {
      //setFilteredData(filteredList);
    }
  }, [filteredList, filteredData, isFilteredDates]);

  useEffect(() => {
    if (filterValues) {
      setStartRange(filterValues.startMonth);
      setEndRange(filterValues.endMonth);
    } else {
      setStartRange("");
      setEndRange("");
    }
  }, [filterValues]);

  useEffect(() => {
    // If no filter is active, always load (for list view)

    setShowLoader(false);
    if (!directDetailPageRenderedID) {
      getExpenseRequestsList();
    }

    // // If filter is active, load filtered data once ranges have values
    // if (isFilterSelected && startRange && endRange) {
    //    setShowLoader(true);
    //    getExpenseRequestsList("filtered", pagination);
    //  }
  }, [currentPath, startRange, endRange, directDetailPageRenderedID]);

  useEffect(() => {
    setShowLoader(true);
    dispatch(getUserListWithDetailThunk())
      .unwrap()
      .then((res) => {
        setUsers(res || []);
      });
    dispatch(getDepartmentListThunk())
      .unwrap()
      .then((res) => {
        setDepartments(res || []);
      });
    dispatch(getProjectCodeListThunk())
      .unwrap()
      .then((res) => {
        setProjectCodes(res || []);
      });
    dispatch(getExpenseCodeListThunk())
      .unwrap()
      .then((res) => {
        setExpenseCodes(res || []);
      });
    dispatch(getCurrencyListDetailThunk())
      .unwrap()
      .then((res) => {
        setAllCurrencies(res || []);
      });
  }, [dispatch, currentPath, storeRole, storeLIUId]);

  useEffect(() => {
    if (!directDetailPageRenderedID) return;

    setSearchText(directDetailPageRenderedID);
    const checkPopup = () =>
      !!document.querySelector(".addExpensePopupFormWraper-visible");

    // Initial check
    if (!checkPopup()) {
      setShowLoader(true);
    }

    if (!autoSearchDone.current) {
      autoSearchDone.current = true;
      handleGlSearch({
        source: "directDetailPageRenderedID",
        value: directDetailPageRenderedID,
      });
    }

    // Watch DOM for popup appearing/disappearing
    const observer = new MutationObserver(() => {
      const popupVisible = checkPopup();

      // Hide loader if popup becomes visible
      if (popupVisible) {
        setShowLoader(false);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [directDetailPageRenderedID, handleGlSearch]);

  useEffect(() => {
    if (directDetailPageRenderedID) return;

    setShowLoader(true);

    const resetPagination = {
      current: 1,
      page: 1,
      pageSize: pagination?.pageSize || 50,
    };

    getExpenseRequestsList("", resetPagination);
  }, [currentStatus]);

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
      className: "all-nowrap-column",
    },
    {
      title: "Name",
      dataIndex: "empName",
      key: "empName",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        return (
          <>
            <div>{text}</div>
            {record?.empID && <small>Emp. Id: {record.empID}</small>}
          </>
        );
      },
    },
    {
      title: "Nature of Expense",
      dataIndex: "natureOfExpense",
      className: "all-nowrap-column",
    },
    {
      title: "Vendor Name",
      dataIndex: "businessPurpose",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        const maxLength = 15;
        const truncatedText =
          text && text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;
        return <span title={text}>{truncatedText}</span>;
      },
    },
    {
      title: "Project Code",
      dataIndex: "projectCodeID",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        const projectCodeInfo = projectCodes.find(
          (projectCodeItem) => projectCodeItem.value == text
        );

        if (typeof projectCodeInfo?.label !== "string") return null;
        const [code = "", companyName = ""] =
          (projectCodeInfo?.label && projectCodeInfo?.label?.split(" - ")) ||
          [];

        return (
          <>
            <div>{code}</div>
            <small>{companyName}</small>
          </>
        );
      },
    },
    {
      title: "Expense Period",
      dataIndex: "expensePeriod",
      className: "all-nowrap-column",
      render: (text) => {
        if (typeof text !== "string") return null;
        const [fromText = "", toText = ""] = (text && text?.split(" - ")) || [];

        const [fromDayMonth = "", fromYear = ""] =
          (fromText && fromText?.split(", ")) || [];
        const [toDayMonth = "", toYear = ""] =
          (toText && toText?.split(", ")) || [];

        return (
          <div className="splitDates">
            <span>
              {fromDayMonth}
              <small>{fromYear}</small>
            </span>
            <span>-</span>
            <span>
              {toDayMonth}
              <small>{toYear}</small>
            </span>
          </div>
        );
      },
    },
    {
      title: "Expense Month",
      dataIndex: "month",
      className: "all-nowrap-column",
    },
    {
      title: "Department Code",
      dataIndex: "departmentID",
      className: "all-nowrap-column",
      render: (text) => {
        let deptId = "";
        let deptName = "";
        let deptCode = "";

        if (text && typeof text === "object") {
          /** CASE 1: departmentID is an object */
          deptId = text._id || "";
          deptName = typeof text.name === "string" ? text.name : "";
          deptCode = typeof text.code === "string" ? text.code : "";
        } else {
          /** CASE 2: departmentID is a string */
          deptId = text;
          const deptInfo = departments.find((d) => d.value == text);

          if (deptInfo?.label) {
            const [name = "", code = ""] = deptInfo.label.split(" - ");
            deptName = name;
            deptCode = code;
          }
        }

        return (
          <>
            <div>{deptName || "-"}</div>
            <small>{deptCode || ""}</small>
          </>
        );
      },
    },
    {
      title: "Expense Code",
      dataIndex: "expenseCodeID",
      className: "all-nowrap-column",
      render: (text) => {
        const expCodeInfo = expenseCodes.find(
          (expCodeItem) => expCodeItem.value == text
        );

        if (typeof expCodeInfo?.label !== "string") return null;
        const [code = "", name = ""] =
          (expCodeInfo?.label && expCodeInfo?.label?.split(" - ")) || [];

        return (
          <>
            <div>{code}</div>
            <small>{name}</small>
          </>
        );
      },
    },
    {
      title: "Currency",
      dataIndex: "currencyID",
      className: "all-nowrap-column",
      render: (text, record) => {
        const currencyInfo = allCurrencies.find(
          (currItem) => currItem.id === text
        );

        if (typeof currencyInfo?.code !== "string") return null;

        return currencyInfo?.code;
      },
    },
    {
      title: "Amount",
      dataIndex: "cost",
      className: "all-nowrap-column",
      render: (text, record) => {
        const currencyInfo = allCurrencies.find(
          (currItem) => currItem.id === record?.currencyID
        );
        return (
          text && (
            <span className="table-btn cursor-unset text-capitalize amount-btn">
              <span>
                {currencyInfo?.symbol_native && currencyInfo?.symbol_native}
                {text}
              </span>
            </span>
          )
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalReimbursement",
      className: "all-nowrap-column",
      render: (text) =>
        text && (
          <span className={`table-btn cursor-unset text-capitalize amount-btn`}>
            <img src={media.documentNormal.svg} alt="Rupee" />
            <span>{text}</span>
          </span>
        ),
    },
    {
      title: "Approvers",
      dataIndex: "approvers",
      render: (approvers) => {
        return (
          <div className="d-flex justify-content-start">
            {approvers &&
              approvers.map((approver, index) => {
                const approverInfo = users.find(
                  (userItem) => userItem.id === approver?.userID
                );

                return (
                  <Tooltip
                    key={index}
                    title={`${approverInfo?.empName.toUpperCase()}, ${
                      approver?.status ? approver?.status : "NOT SENT"
                    }`}
                    className={`approvers-status-tooltip mr-5`}
                  >
                    <span>
                      {approverInfo?.profilePicUrl ? (
                        <>
                          <img
                            src={
                              process.env.REACT_APP_BACKEND +
                              "/images/" +
                              approverInfo?.profilePicUrl
                            }
                            alt="card icon"
                          />
                        </>
                      ) : (
                        <span className="initialLetter">
                          {approverInfo?.empName
                            ? approverInfo?.empName.charAt(0).toUpperCase()
                            : "?"}
                        </span>
                      )}
                      <b
                        className={`approvers-tooltips ${
                          approver?.status
                            ? approver?.status?.toLowerCase()
                            : "yetToSent"
                        }-tooltips`}
                      ></b>
                    </span>
                  </Tooltip>
                );
              })}
          </div>
        );
      },
    },
    {
      title: "Admin Action",
      dataIndex: "adminApprover",
      className: "all-nowrap-column",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      className: "all-nowrap-column",
      render: (text) => (
        <span
          className={`table-btn cursor-unset text-capitalize ${text?.toLowerCase()}-btn`}
        >
          {text?.toLowerCase() ===
          process.env.REACT_APP_SEEK_ACK_STATUS.replace(" ", "")?.toLowerCase()
            ? process.env.REACT_APP_SEEK_ACK_STATUS
            : text?.toLowerCase() ===
              process.env.REACT_APP_ACK_REC_STATUS.replace(
                " ",
                ""
              )?.toLowerCase()
            ? process.env.REACT_APP_ACK_REC_STATUS
            : text?.toLowerCase() ===
              process.env.REACT_APP_NORESPONSE_STATUS.replace(
                " ",
                ""
              )?.toLowerCase()
            ? process.env.REACT_APP_NORESPONSE_STATUS
            : text?.toLowerCase()}
        </span>
      ),
    },
    {
      title: "Payment Paid On",
      dataIndex: "paymentPaidOn",
      className: "all-nowrap-column",
      key: "paymentPaidOn",
      render: (date, record) => {
        if (!moment(date).isValid()) return "-";

        return (
          <>
            {moment(date).format("Do MMM, YYYY")}
            {record?.modeOfPayment && (
              <>
                <small>
                  <strong>(Mode of Payment: </strong> {record?.modeOfPayment})
                </small>
              </>
            )}
          </>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => {
        const draft =
          record.status === process.env.REACT_APP_DRAFT_STATUS.toUpperCase() &&
          record.empID === storeEmpID
            ? 1
            : 0;
        const link =
          draft === 1
            ? `/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}/draft/${record?.key}`
            : `/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}/${record?.key}`;
        const viewTitle = draft === 1 ? "View Draft" : "View";

        return (
          <>
            <div className="table-menu-container">
              <div className="table-menu-icon text-grey-custom">⋯</div>
              <div className="table-dropdown-menu">
                <div className="table-menu-item">
                  <div
                    className="text-grey-custom text-size-12 viwLink cursor-pointer"
                    title={`${viewTitle}`}
                    onClick={() => handleView(record?.key, viewTitle)}
                  >
                    {record?.key}
                    <i className="flaticon-eye icon-lg text-grey-custom"></i>{" "}
                    {`${viewTitle}`}
                  </div>
                </div>

                <div
                  className="table-menu-item"
                  onClick={() => handlePdfExport(record)}
                >
                  <i
                    className="fas fa-file-pdf icon-lg text-grey-custom cursor-pointer"
                    title="Download PDF"
                  ></i>{" "}
                  <span className="text-grey-custom">Download PDF</span>
                </div>
                {draft === 1 && (
                  <div className="table-menu-item">
                    <div
                      title="Publish"
                      onClick={() => {
                        handleDraftExpenseForPublish(record?.key);
                      }}
                    >
                      <i
                        className="fas fa-paper-plane icon-lg text-grey-custom cursor-pointer"
                        aria-hidden="true"
                      ></i>{" "}
                      <span className="text-grey-custom">Publish</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      },
    },
  ];

  const detailPageRenderSource =
    directDetailPageRenderedID &&
    (currentPath?.split("/")?.[0] ===
      process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN ||
      currentPath?.split("/")?.[0] ===
        process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN)
      ? false
      : true;

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
      <div className="container">
        {/* <CustomLoader showLoader={showLoader} /> */}
        <div className="bgi-no-repeat card-stretch rounded-lg">
          <TopContainerHeader
            sectionNameClass="col-lg-7 text-left commonTitleText"
            sectionTitle="Expense Listing"
            sectionDescription={
              showLoader ? "Loading..." : `${storeTotalCount} Found`
            }
            hasSectionButton={false}
            onFilteredData={setFilteredData}
            hasFilter={true}
            listingStatus={true}
            handleMonthRangeChange={handleMonthRangeChange}
            handleDownloadMWERReport={handleDownloadMWERReport}
            isGlSearch={true}
            onSearchValue={(value) => {
              setShowLoader(true);

              handleGlSearch({ value }).finally(() => setShowLoader(false));
            }}
            searchText={searchText}
            setSearchText={setSearchText}
            hasSearch={true}
            hasSearchButton="general"
            searchClassName="table-search dashboardSearch"
            searchPlaceholder="Search Name, Nature of Expense, EmpID, Vendor Name & Total Amount"
            searchData={listOfExpenses}
            searchKeys={[
              "empName",
              "natureOfExpense",
              "empID",
              "vendorName",
              "totalAmount",
            ]}
            handleStatusChange={handleTabClick}
          />

          <div
            className={`card p-5 rounded-lg paddingTop-0 ${
              storeTotalCount <=
              process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
                ? "paddingBottom-0"
                : "minHeight-100vh"
            }`}
          >
            <div className="table-area">
              <div className="table-responsive">
                <div className="tab-content" id="alltable-size">
                  <TableContent
                    storeList={filteredData}
                    columns={columns}
                    bordered={false}
                    className="expenseMultitasking"
                    rowClassName="editable-row"
                    loading={showLoader}
                    pagination={pagination}
                    totalCount={storeTotalCount}
                    //handlePaginationChange={onPageChange}
                    handlePaginationChange={handlePaginationChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isVisible &&
        (actionViewSelected === "View" ||
          (directDetailPageRenderedID &&
            currentPath?.split("/")?.[0] ===
              process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN)) && (
          <ExpenseListingDetail
            detailPageRenderSource={detailPageRenderSource}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            recordKey={selectedRecordKey}
            onCloseReset={() => {
              //setSearchText("");
              //handleGlSearch("", ""); // reset list in parent
            }}
          />
        )}

      {isVisible && actionViewSelected === "View Draft" && (
        <DraftExpenseRequest
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          recordKey={selectedRecordKey}
        />
      )}
    </>
  );
}
